import { useState } from 'react';
import { abrirRota, abrirSite, localDaTarefa, separarLocal } from '../lib/mapa';
import { Alert, Pressable, Text, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import type { Task } from '../store/types';
import { toggleDone, toggleSub, addSub, deleteTask, excluirTarefa, newTask, saveTask, mudadaNaData, naData, salvarOcorrencia } from '../store/planner';
import { getDoc } from '../store/store';
import { emojiDaPausa, pausasAgora } from '../lib/pausas';
import { MiniTemporizador } from './MiniTemporizador';
import { uid } from '../lib/date';
import { ActionSheet, type Action } from './ui';
import { AcoesItem } from './Acoes';
import { useOrganizar } from '../lib/organizar';
import { useDoc } from '../store/store';
import { linkHref, linkLabel, linkExists } from '../store/links';
import { isRepeating } from '../lib/recur';
import { durLabel, endTime, toMin } from '../lib/time';
import { noAparelho, nomeFuso } from '../lib/fuso';
import { DoneMark, TimerShape } from './Shapes';
import { C, alpha, aba, useCores } from './theme';
import { opcoesOnde, useTaskSheet } from './TaskSheet';
import { t } from '../lib/i18n';
import { horaTela } from '../lib/date';
import { useCaixas } from '../lib/caixas';

/** Uma tarefa na linha do tempo: marcar concluída à esquerda, cartão no meio, temporizador à direita. */
export function TaskCard({ t: tk, date, now, compact }: { t: Task; date: string; now: Date; compact?: boolean }) {
  const router = useRouter();
  const sheet = useTaskSheet();
  const aj = useDoc('ajustes');
  const m = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const { corDe, liberada } = useCores();
  const [abrirSubs, setAbrirSubs] = useState(false);
  const [nova, setNova] = useState('');
  const [onde, setOnde] = useState<Action[] | null>(null);
  const cor = tk.color ?? corDe(tk.area);
  const feito = tk.done.includes(date);
  const subFeitas = (tk.subDone[date] ?? []).filter(id => tk.subtasks.some(s => s.id === id)).length;

  // hora no relógio deste aparelho (convertida, se a tarefa foi marcada em outro fuso)
  const loc = noAparelho(tk, date);
  const hora = loc.hora;
  // progresso do tempo
  let pct = feito ? 1 : 0, resta = '';
  const ehHoje = date === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (!feito && hora && ehHoje) {
    const agora = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    const ini = toMin(hora);
    pct = Math.max(0, Math.min(1, (agora - ini) / tk.duration));
    if (pct > 0 && pct < 1) resta = t('Resta {tempo}', { tempo: durLabel(Math.ceil(ini + tk.duration - agora)) });
  }
  const rodando = !!resta;
  // 0.67 (A1–A3): tarefa com pausas acontecendo agora: mini temporizador, barrinha e emoji da pausa
  const emojiPausa = emojiDaPausa(tk, aj);
  const pz = !compact && !feito && ehHoje ? pausasAgora(tk, date, now.getTime()) : null;
  const emPausa = pz?.estado.trecho?.tipo === 'pausa';
  const altura = compact || !tk.time ? undefined : m.alt(Math.max(76, Math.min(200, 56 + tk.duration * 0.9)));

  /** "+ Subtarefa" no cartão (B1): na tarefa que se repete, pergunta onde aplicar, como na ficha. */
  function novaSub(texto: string) {
    const serie = getDoc('planner').tasks.find(x => x.id === tk.id);
    if (!serie || !isRepeating(serie)) { addSub(tk.id, texto, date); setNova(''); return; }
    const aberta = naData(serie, date);
    const editada: Task = { ...aberta, subtasks: [...aberta.subtasks, { id: uid(), text: texto.trim() }] };
    setOnde(opcoesOnde(date, false, o => { salvarOcorrencia(editada, aberta, date, o); setNova(''); }));
  }

  function abrirLink() {
    if (!tk.link) return;
    if (!liberada(tk.link.aba)) return Alert.alert(t('{aba} bloqueada', { aba: aba(tk.link.aba).nome }), t('Assine a aba para abrir o vínculo.'));
    if (!linkExists(tk.link)) return Alert.alert(t('Vínculo perdido'), t('O item vinculado foi apagado ou movido. Edite a tarefa para escolher outro.'));
    router.push(linkHref(tk.link, date) as any);
  }

  const local = localDaTarefa(tk.notes);
  // link de reunião nas anotações (Zoom, Meet, Teams, Webex, Jitsi): botão para entrar
  const reuniao = tk.notes.match(/https?:\/\/[^\s]*(zoom\.us|meet\.google\.com|teams\.microsoft\.com|teams\.live\.com|webex\.com|meet\.jit\.si|whereby\.com)[^\s]*/i)?.[0] ?? '';
  const tags: string[] = [];
  if (separarLocal(tk.notes).resto.trim()) tags.push('📝');
  if (tk.anexos?.length) tags.push('📎' + tk.anexos.length);
  tags.push(tk.energy ? `⚡${tk.energy}` : '🌙');
  if (isRepeating(tk)) tags.push(mudadaNaData(tk, date) ? '🔁✎' : '🔁');   // ✎: mudada só hoje/só esta semana (B2)
  if (tk.pauses.length && tk.time) tags.push(`${emojiPausa}${tk.pauses.length}`);
  if (tk.gcal) tags.push('📆');

  return (
    <View style={{ flexDirection: 'row', gap: m.gap(10), alignItems: 'flex-start' }}>
      <Pressable hitSlop={10} onPress={() => toggleDone(tk.id, date)} style={{ paddingTop: m.pad(14) }}>
        <DoneMark shape={aj.concluir} done={feito} color={cor} size={m.v(30, 28, 24)} />
      </Pressable>

      <View style={{ flex: 1, gap: m.gap(6) }}>
        <Pressable onPress={() => sheet.open({ task: tk, date })} style={{ minHeight: altura, borderRadius: m.raio(18), ...m.pads(14), gap: m.gap(6),
          backgroundColor: rodando ? alpha(cor, 0.16) : C.card, borderWidth: 1, borderColor: rodando ? alpha(cor, 0.6) : C.line, opacity: feito ? 0.55 : 1 }}>
          {hora && (
            <Text style={{ color: rodando ? cor : C.sub, fontSize: 12, fontWeight: '600' }}>
              {horaTela(hora)} – {horaTela(endTime(hora, tk.duration))} · {durLabel(tk.duration)}{loc.dia !== date ? ` (${loc.dia > date ? '+1' : '-1'} ${t('dia')})` : ''}{resta ? ` · ${resta}` : ''}
            </Text>
          )}
          {loc.outro && tk.tz && <Text style={{ color: C.mute, fontSize: 11 }}>🌐 {t('{hora} em {fuso}', { hora: horaTela(tk.time!), fuso: nomeFuso(tk.tz) })}</Text>}
          <Text style={{ color: C.text, fontSize: 16, fontWeight: '700', textDecorationLine: feito ? 'line-through' : 'none' }} numberOfLines={2}>
            {tk.emoji} {tk.title}
          </Text>
          {(!!local || !!reuniao) && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {!!local && (
                <Pressable onPress={() => abrirRota(local)} hitSlop={6} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10, backgroundColor: C.card2, maxWidth: '100%' }}>
                  <Text style={{ color: C.sub, fontSize: 12, flexShrink: 1 }} numberOfLines={1}>📍 {local}</Text>
                  <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>· {t('Rota')}</Text>
                </Pressable>
              )}
              {!!reuniao && (
                <Pressable onPress={() => abrirSite(reuniao)} hitSlop={6} style={{ paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10, backgroundColor: C.card2 }}>
                  <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>🎥 {t('Entrar na reunião')}</Text>
                </Pressable>
              )}
            </View>
          )}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {tk.subtasks.length > 0 && (
              <Pressable onPress={() => setAbrirSubs(v => !v)} hitSlop={6} style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, backgroundColor: alpha(cor, 0.2) }}>
                <Text style={{ color: C.text, fontSize: 12 }}>☑ {subFeitas}/{tk.subtasks.length} {abrirSubs ? '▴' : '▾'}</Text>
              </Pressable>
            )}
            {tags.map(g => <Text key={g} style={{ color: C.sub, fontSize: 12 }}>{g}</Text>)}
            {tk.link && (
              <Text style={{ color: liberada(tk.link.aba) ? cor : C.mute, fontSize: 12 }} numberOfLines={1}>
                🔗 {liberada(tk.link.aba) ? linkLabel(tk.link) : aba(tk.link.aba).nome}
              </Text>
            )}
          </View>
          {pz && <MiniTemporizador trechos={pz.trechos} inicioMs={pz.inicioMs} cor={cor} emojiPausa={emojiPausa} agoraMs={now.getTime()} />}
        </Pressable>

        {abrirSubs && (
          <View style={{ backgroundColor: C.card, borderRadius: m.raio(14), padding: m.pad(8), marginLeft: 8 }}>
            {tk.subtasks.map(s => {
              const ok = (tk.subDone[date] ?? []).includes(s.id);
              return (
                <Pressable key={s.id} onPress={() => toggleSub(tk.id, s.id, date)} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', ...m.padV(8) }}>
                  <View style={{ width: 20, height: 20, borderRadius: 6, borderWidth: 2, borderColor: ok ? cor : C.mute, backgroundColor: ok ? cor : 'transparent',
                    alignItems: 'center', justifyContent: 'center' }}>{ok && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}</View>
                  <Text style={{ color: C.text, flex: 1, textDecorationLine: ok ? 'line-through' : 'none', opacity: ok ? 0.6 : 1 }}>{s.text}</Text>
                </Pressable>
              );
            })}
            <TextInput value={nova} onChangeText={setNova} placeholder={t('+ Subtarefa')} placeholderTextColor={C.mute} blurOnSubmit={false}
              style={{ color: C.text, ...m.padV(8) }} onSubmitEditing={() => { if (nova.trim()) novaSub(nova); }} />
          </View>
        )}
        <ActionSheet title={t('Esta tarefa se repete. Onde aplicar a mudança?')} actions={onde} onClose={() => setOnde(null)} />
      </View>

      {tk.time ? (
        <Pressable onPress={tk.link ? abrirLink : undefined} disabled={!tk.link} style={{ paddingTop: m.pad(10) }}>
          <TimerShape shape={aj.timer} pct={pct} color={cor} icon={emPausa ? emojiPausa : tk.emoji} size={m.v(46, 42, 36)} />
        </Pressable>
      ) : <View style={{ width: m.v(46, 42, 36) }} />}
    </View>
  );
}

/** Tarefa com as ações universais (0.52; 0.65): direita exclui (lixeira de 30 dias), esquerda arquiva ou duplica;
 *  no ⋮: Enviar para outra aba e Fixar no topo (só as tarefas sem horário). */
export function TaskCardAcoes(props: { t: Task; date: string; now: Date; compact?: boolean }) {
  const tk = props.t, date = props.date;
  const [del, setDel] = useState<Action[] | null>(null);
  const org = useOrganizar('planner');
  // texto 05 C: tudo vai para a Lixeira (a série inteira, o dia ou a data de fim de antes), com o Desfazer
  const tudo = () => excluirTarefa(tk.id, 'all', date);
  const excluir = () => {
    if (!isRepeating(tk)) {
      Alert.alert(t('Excluir tarefa?'), [tk.title, t('Fica 30 dias na lixeira.')].join('\n\n'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: tudo }]);
      return;
    }
    setDel([
      { label: t('Somente esta tarefa'), onPress: () => excluirTarefa(tk.id, 'one', date) },
      { label: t('Esta e todas as futuras'), onPress: () => excluirTarefa(tk.id, 'future', date) },
      { label: t('Todas (passadas, esta e futuras)'), onPress: tudo, destructive: true },
    ]);
  };
  const duplicar = () => {
    const agora = new Date().toISOString();
    const tk = getDoc('planner').tasks.find(x => x.id === props.t.id) ?? props.t;   // a série, sem a mudança "só hoje" (0.67)
    saveTask(newTask({ ...tk, gcal: undefined, id: uid(), title: t('{titulo} (cópia)', { titulo: tk.title.trim() }), done: [], subDone: {}, skip: [],
      createdAt: agora, updatedAt: agora, ordem: tk.ordem != null ? tk.ordem + 0.5 : undefined, subtasks: tk.subtasks.map(x => ({ ...x, id: uid() })) }));
  };
  return (
    <>
      <AcoesItem lembrar={{ aba: 'planner', id: tk.id, titulo: tk.title }} nome={tk.title} onExcluir={excluir} semConfirmar onDuplicar={duplicar} onArquivar={() => org.arquivar(tk.id)} menuPos="baixo"
        fixar={!tk.time ? { on: org.fixado(tk.id), alternar: () => org.alternarFixo(tk.id) } : undefined}
        pacote={() => ({ titulo: tk.title, emoji: tk.emoji, itens: tk.subtasks.map(s => s.text), texto: separarLocal(tk.notes).resto.trim(),
          data: date, hora: tk.time, origem: 'planner', remover: () => deleteTask(tk.id, isRepeating(tk) ? 'one' : 'all', date) })}>
        <TaskCard {...props} />
      </AcoesItem>
      <ActionSheet title={t('Excluir tarefa repetida')} actions={del} onClose={() => setDel(null)} />
    </>
  );
}
